import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ItemRequest,
    MediaUpload,
    createCampaignItem,
    fetchCampaignItem,
    fetchCampaignItems,
    updateCampaignItem,
} from 'api/campaigns';
import * as CampaignItemForm from 'assets/js/pages/campaign-settings/campaign-event/CampaignItemForm';
import { CampaignItemBundleFormData } from 'assets/js/pages/campaign-settings/campaign-event/types';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Tooltip from 'components/tooltip';
import { UploadFile } from 'components/upload';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItemBundle } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const EDITOR_MIN_HEIGHT = 200;

const CampaignItemBundleFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const { bundleId } = useParams<{ bundleId: string }>();
    const isEditing = !!bundleId;
    const campaignId = campaign?.id ?? 0;
    const { taxDeductibilitySettings } = useFlags();

    const { isBeta } = useCurrentAccount();
    const aiEnabled = isBeta;

    const { data: eventTicketsUnfiltered } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, {
            is_auction: false,
            is_bundle: false,
        }),
        queryFn: () =>
            fetchCampaignItems({
                campaignId: campaign.id,
                is_auction: false,
                is_bundle: false,
            }),
    });

    const hasTickets = eventTicketsUnfiltered.data.length !== 0;
    const totalTickets = eventTicketsUnfiltered.meta.total;

    const { data: bundle } = useSuspenseQuery({
        queryKey: campaignQueryKeys.item(campaignId, Number(bundleId)),
        queryFn: () =>
            conditionallyFetchOrNull(
                () =>
                    fetchCampaignItem<CampaignItemBundle>({
                        campaignId,
                        itemId: Number(bundleId),
                    }),
                isEditing,
            ),
    });

    const formMethods = Form.useGivebutterForm<CampaignItemBundleFormData>({
        defaultValues: defaultForm(bundle),
    });
    const { control, handleSubmit, reset, resetFormButton, setFocus, setError, watch, path } =
        formMethods;

    const [isFree, isUnlimited] = watch(['free', 'unlimited']);

    const [shouldExitAfterCreate, setShouldExitAfterCreate] = useState(true);

    const invalidate = async () => {
        await queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.items(campaignId),
            type: !isEditing ? 'all' : undefined,
            exact: false,
        });
        await queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.item(campaignId, Number(bundleId)),
        });
    };

    const onSuccess = async () => {
        toast.success(`Bundle ${isEditing ? 'updated' : 'created'}.`);
        await invalidate();
        reset({
            ...defaultForm(bundle),
        });
        shouldExitAfterCreate ? goBack() : setFocus('name');
    };

    const onError = (error: GivebutterAxiosError) => {
        const data = error.response?.data;
        const errors = data?.errors;

        if (typeof errors === 'object') {
            for (const field in errors) {
                if (field === 'items') {
                    errors[field].map((err: string, index: number) =>
                        setError(`items.${index}.value`, {
                            type: 'server',
                            message: err,
                        }),
                    );
                    continue;
                }

                setError(field as keyof CampaignItemBundleFormData, {
                    type: 'server',
                    message: errors[field][0],
                });
            }
        }

        toast.error(data?.message ?? 'An unknown error occurred');
        resetFormButton();
    };

    const { mutate: createBundle, isPending: isCreating } = useMutation({
        mutationFn: async (data: ItemRequest) => {
            return createCampaignItem({ campaignId: campaignId, data, type: 'bundle' });
        },
        onError,
        onSuccess,
    });

    const { mutate: updateBundle, isPending: isUpdating } = useMutation({
        mutationFn: (data: ItemRequest) => {
            return updateCampaignItem({ campaignId: campaignId, itemId: Number(bundleId), data });
        },
        onError,
        onSuccess,
    });

    const onSubmit: SubmitHandler<CampaignItemBundleFormData> = data => {
        if (isEditing) {
            updateBundle(toRequest(data));
        } else {
            createBundle(toRequest(data));
        }
    };

    return (
        <Form.Provider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className={'contents'}>
                <Drawer.Header>
                    <Drawer.Title>{isEditing ? 'Update' : 'New'} bundle</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        {isEditing && Number(bundle?.sold) > 0 && (
                            <AlertNotification.Root type="info">
                                <AlertNotification.Icon />
                                <AlertNotification.Content>
                                    <AlertNotification.Title>
                                        This bundle has already been purchased by a supporter
                                    </AlertNotification.Title>
                                    <AlertNotification.Description>
                                        Any changes made will not alter previous purchases and will
                                        only reflect on future bundle purchases.
                                    </AlertNotification.Description>
                                </AlertNotification.Content>
                            </AlertNotification.Root>
                        )}
                        <div>
                            <h2 className={'text-base font-medium'}>Bundle details</h2>
                            <p className="text-sm text-gray-600">
                                Bundles can be configured independent of the individual tickets you
                                choose to include.
                            </p>
                        </div>
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Bundle name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('name')}
                                control={control}
                                rules={{ required: 'The name is required.' }}
                            >
                                <Form.TextInputForm placeholder={'Table for 5'} />
                            </Form.TextInputRoot>
                            <Form.UseFormError name={path('name')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('description')}>Description</Form.Label>
                            <Wysiwyg.Root>
                                <UseFormWysiwyg
                                    name={'description'}
                                    control={control}
                                    inline={false}
                                    init={{
                                        min_height: EDITOR_MIN_HEIGHT,
                                        statusbar: false,
                                        placeholder:
                                            'Include your friends and family in enjoying a night of culinary delights in this bundled general admission tickets package.',
                                        plugins: [
                                            ...(aiEnabled ? ['ai'] : []),
                                            ...Wysiwyg.Constants.DESCRIPTION_EDITOR_PLUGINS,
                                            'autoresize',
                                        ],
                                        toolbar: `aidialog aishortcuts ${Wysiwyg.Constants.DESCRIPTION_EDITOR_TOOLBAR}`,
                                        content_style: Wysiwyg.Constants.APP_FONT,
                                        menubar: false,
                                        link_target_list: false,
                                        link_assume_external_targets: true,
                                    }}
                                />
                            </Wysiwyg.Root>
                            <Form.UseFormError name={path('description')} />
                        </Form.Container>

                        <div className="flex gap-4">
                            <Form.Container className="min-w-0">
                                <Form.Label name={path('price')}>
                                    Price
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                {!isFree ? (
                                    <>
                                        <Form.TextInputRoot
                                            name={path('price')}
                                            control={control}
                                            rules={{
                                                required: !isFree
                                                    ? 'Must provide a price or choose "Free".'
                                                    : false,
                                            }}
                                            inputRootProps={{
                                                mask: 'decimal',
                                            }}
                                        >
                                            <InputCurrencyIcon />
                                            <Form.TextInputForm placeholder={'100'} />
                                        </Form.TextInputRoot>
                                        <Form.UseFormError name={path('price')} />
                                    </>
                                ) : (
                                    <p
                                        className={
                                            'h-10 cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 leading-none text-gray-400'
                                        }
                                    >
                                        Free
                                    </p>
                                )}
                                <div className={'flex items-center gap-x-2'}>
                                    <Form.UseFormCheckboxInput
                                        name={path('free')}
                                        control={control}
                                    />
                                    <Form.Label name={path('free')}>Free</Form.Label>
                                </div>
                            </Form.Container>
                            <Form.Container className="min-w-0">
                                <Form.Label name={path('total_quantity')}>
                                    Bundle quantity
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                {!isUnlimited ? (
                                    <>
                                        <Form.TextInputRoot
                                            name={path('total_quantity')}
                                            control={control}
                                            rules={{
                                                required: !isUnlimited
                                                    ? 'Must provide a quantity or choose "Unlimited".'
                                                    : false,
                                                min: {
                                                    value: 1,
                                                    message: 'The quantity must be at least 1.',
                                                },
                                            }}
                                            inputRootProps={{
                                                disabled: isUnlimited,
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm placeholder={'250'} />
                                        </Form.TextInputRoot>
                                        <Form.UseFormError name={path('total_quantity')} />
                                    </>
                                ) : (
                                    <p
                                        className={
                                            'h-10 cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 leading-none text-gray-400'
                                        }
                                    >
                                        Unlimited
                                    </p>
                                )}
                                <div className={'flex items-center gap-x-2'}>
                                    <Form.UseFormCheckboxInput
                                        name={path('unlimited')}
                                        control={control}
                                    />
                                    <Form.Label
                                        name={path('unlimited')}
                                        className={'cursor-pointer'}
                                    >
                                        Unlimited
                                    </Form.Label>
                                </div>
                            </Form.Container>
                        </div>
                        {taxDeductibilitySettings && (
                            <div className="flex w-1/2 gap-4">
                                <Form.Container>
                                    <Form.Label
                                        name="retail_price"
                                        className="flex items-center gap-1"
                                    >
                                        Fair market value
                                        <Tooltip.Root>
                                            <Tooltip.Trigger>
                                                <FontAwesomeIcon icon={faQuestionCircle} />
                                                <span className="sr-only">More info</span>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse>
                                                    <Tooltip.Arrow inverse />
                                                    <p>
                                                        Bundles will only show a tax-deductible
                                                        amount on receipts and end-of-year
                                                        statements if a Fair Market Value is
                                                        provided. The tax-deductible amount will be
                                                        the Bundle Price minus Fair Market Value,
                                                        per IRS guidelines.
                                                    </p>
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Form.Label>

                                    <Form.TextInputRoot
                                        name="retail_price"
                                        control={control}
                                        inputRootProps={{ mask: 'decimal' }}
                                        disabled={isFree}
                                    >
                                        <InputCurrencyIcon />
                                        {!isFree && <Form.TextInputForm placeholder="0" />}
                                    </Form.TextInputRoot>
                                    <p className="text-sm text-gray-600">
                                        Actual bundle value for tax purposes.
                                    </p>
                                    <Form.UseFormError name={path('retail_price')} />
                                </Form.Container>
                            </div>
                        )}
                        <section className={'flex justify-between gap-y-1'}>
                            <div className={'flex flex-col gap-y-1'}>
                                <Form.Label name={path('active')}>Set as available</Form.Label>
                                <p className={'text-sm leading-5 text-gray-600'}>
                                    This bundle will be available for purchase on your campaign and
                                    will be unavailable when the quantity of the remaining tickets
                                    is less than the quantity included in the bundle.
                                </p>
                            </div>
                            <Form.Switch name={path('active')} control={control} />
                        </section>
                        <Separator className={`-mx-8 !w-auto`} />

                        <CampaignItemForm.Root hasTickets={hasTickets} totalItems={totalTickets}>
                            <CampaignItemForm.SelectItemHeader />
                            <CampaignItemForm.SelectItem />
                            <Separator className={`-mx-8 !w-auto`} />
                            <CampaignItemForm.SelectBundleSummary />
                        </CampaignItemForm.Root>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'transparent'}>Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            {!isEditing && (
                                <Form.SubmitButton
                                    variant={'outline'}
                                    color={'neutral'}
                                    noAnimation={true}
                                    onClick={() => setShouldExitAfterCreate(false)}
                                    isSubmitting={isCreating}
                                >
                                    Create and add another
                                </Form.SubmitButton>
                            )}
                            <Form.SubmitButton
                                isSubmitting={isCreating || isUpdating}
                                onClick={() => setShouldExitAfterCreate(true)}
                            >
                                {isEditing ? 'Save edits' : 'Create'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const defaultForm = (bundle: CampaignItemBundle | null) => {
    if (!bundle) {
        return {
            name: '',
            active: true,
            price: '',
            retail_price: '',
            free: false,
            total_quantity: '',
            unlimited: false,
            description: '',
            items: [CampaignItemForm.emptyItemField],
            pictures: [],
        };
    }

    return {
        name: bundle.name,
        active: bundle.active,
        price: maskDecimal(bundle.price),
        retail_price: maskDecimal(bundle.retail_price),
        free: Number(bundle.price) === 0,
        total_quantity:
            bundle.total_quantity === null ? '' : maskWholeNumber(bundle.total_quantity),
        unlimited: bundle.total_quantity === null,
        description: bundle.description || '',
        items: bundle?.bundle_items?.length
            ? bundle.bundle_items?.map(item => {
                  return {
                      value: item.name ?? '',
                      id: item.id.toString(),
                      quantity: item.quantity_per_bundle?.toString(),
                      order: item.bundle_order,
                      total_quantity: item.total_quantity,
                      bundle_only: item.bundle_only,
                      remaining: item.remaining,
                  };
              })
            : [CampaignItemForm.emptyItemField],
        pictures: bundle?.pictures as unknown as UploadFile[],
    };
};

const toRequest = (form: CampaignItemBundleFormData) => {
    let price = null;

    if (form.free) {
        price = 0;
    } else if (form.price) {
        price = unmaskNumber(form.price);
    }

    return {
        name: form.name,
        active: form.active,
        price,
        retail_price: form.retail_price && !form.free ? unmaskNumber(form.retail_price) : null,
        total_quantity: form.unlimited ? null : unmaskNumber(form.total_quantity),
        description: form.description,
        bundle_items: form.items[0]?.quantity
            ? CampaignItemForm.toCampaignItemsRequest(form.items)
            : [],
        pictures: form.pictures as unknown as MediaUpload[],
    };
};

export type { CampaignItemBundleFormData };
export { CampaignItemBundleFormDrawer };
